import { Head, Link, usePage } from "@inertiajs/react"
import { Fragment, type ReactNode } from "react"

import { AppSidebar } from "@/components/app-sidebar"
import { InertiaProgressBar } from "@/components/inertia-progress"
import { InstallAppBanner, InstallAppProvider } from "@/components/install-app"
import { PullToRefresh } from "@/components/pull-to-refresh"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Separator } from "@/components/ui/separator"
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar"

export function ApplicationLayout({ children }: { children: ReactNode }) {
  const { props } = usePage()
  const title = props.title
  const breadcrumbs = props.breadcrumbs

  return (
    <InstallAppProvider>
      <Head title={title ?? undefined} />
      <InertiaProgressBar />
      <PullToRefresh />
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <div
            aria-hidden
            className="h-[env(safe-area-inset-top)] shrink-0 bg-sidebar"
          />
          <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
            <div className="flex items-center gap-2 px-4">
              <SidebarTrigger className="-ml-1 size-11 md:size-8" />
              <Separator
                orientation="vertical"
                className="mr-2 data-vertical:h-4 data-vertical:self-auto"
              />
              <Breadcrumb>
                <BreadcrumbList>
                  {breadcrumbs.map((crumb, i) => {
                    const isLast = i === breadcrumbs.length - 1
                    return (
                      <Fragment key={`${crumb.path}-${i}`}>
                        <BreadcrumbItem>
                          {isLast ? (
                            <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                          ) : (
                            <BreadcrumbLink asChild>
                              <Link href={crumb.path}>{crumb.label}</Link>
                            </BreadcrumbLink>
                          )}
                        </BreadcrumbItem>
                        {!isLast && <BreadcrumbSeparator />}
                      </Fragment>
                    )
                  })}
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          </header>
          <InstallAppBanner />
          <div className="flex flex-1 flex-col gap-6 p-4 pt-0 pb-[max(1rem,env(safe-area-inset-bottom))]">
            {children}
          </div>
        </SidebarInset>
      </SidebarProvider>
    </InstallAppProvider>
  )
}
